import { useEffect, useState } from "react";
import {
  formatWeekLabel,
  getWeekKey,
  loadAvailableWeeks,
  loadDuelRecords,
  loadHighscores,
  type DuelRecord,
  type HighscoreEntry,
} from "../game/storage";
import {
  KUMON_CATEGORIES,
  type KumonCategoryId,
  type KumonGameConfig,
  type KumonLevel,
} from "../game/kumonTypes";

interface KumonMenuProps {
  onStart: (config: KumonGameConfig) => void;
  onNavigateToClassic: () => void;
}

export function KumonMenu({ onStart, onNavigateToClassic }: KumonMenuProps) {
  const [mode, setMode] = useState<"solo" | "duel">("solo");
  const [selectedCatId, setSelectedCatId] = useState<KumonCategoryId>("kumon_3a");
  const [p1Name, setP1Name] = useState("Player 1");
  const [p2Name, setP2Name] = useState("Player 2");
  const [highscores, setHighscores] = useState<HighscoreEntry[]>([]);
  const [duels, setDuels] = useState<DuelRecord[]>([]);

  // Active level tab
  const activePreset = KUMON_CATEGORIES.find((c) => c.id === selectedCatId) ?? KUMON_CATEGORIES[0]!;
  const [activeLevelTab, setActiveLevelTab] = useState<KumonLevel>(activePreset.level);

  // Leaderboard Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalCatId, setModalCatId] = useState<KumonCategoryId>("kumon_3a");
  const [modalWeek, setModalWeek] = useState<string>(getWeekKey());
  const [availableWeeks, setAvailableWeeks] = useState<string[]>([getWeekKey()]);
  const [modalScores, setModalScores] = useState<HighscoreEntry[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  useEffect(() => {
    loadHighscores(selectedCatId, "kumon").then(setHighscores);
  }, [selectedCatId]);

  useEffect(() => {
    loadDuelRecords().then(setDuels);
    loadAvailableWeeks().then(setAvailableWeeks);
  }, []);

  useEffect(() => {
    if (showModal) {
      loadHighscores(modalCatId, "kumon", modalWeek).then(setModalScores);
      loadAvailableWeeks().then(setAvailableWeeks);
    }
  }, [showModal, modalCatId, modalWeek]);

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const openLeaderboard = (catId?: KumonCategoryId) => {
    setModalCatId(catId ?? selectedCatId);
    setModalWeek(getWeekKey());
    setShowModal(true);
  };

  const start = () => {
    onStart({
      mode,
      selectedCategory: selectedCatId,
      player1Name: p1Name.trim() || "Player 1",
      player2Name: p2Name.trim() || "Player 2",
    });
  };

  // Group categories by Level
  const level3ACats = KUMON_CATEGORIES.filter((c) => c.level === "3A");
  const level2ACats = KUMON_CATEGORIES.filter((c) => c.level === "2A");
  const levelACats = KUMON_CATEGORIES.filter((c) => c.level === "A");
  const levelBCatsAdd = KUMON_CATEGORIES.filter((c) => c.level === "B" && c.subMode === "add");
  const levelBCatsSub = KUMON_CATEGORIES.filter((c) => c.level === "B" && c.subMode === "sub");

  return (
    <div className="menu kumon-menu">
      <header className="menu-header">
        <div className="menu-title-group">
          <div className="kumon-badge-pill">KUMON MATH SPEED</div>
          <h1 className="title">
            KUMON<span className="title-accent">WAR</span>
          </h1>
          <p className="subtitle">ฝึกคิดเลขเร็วตามแบบฝึกหัดคุมอง · แข่งขันวัดความเร็ว</p>
        </div>

        <div className="header-right-actions">
          <button
            type="button"
            className="nav-switch-btn"
            onClick={onNavigateToClassic}
            title="สลับไปยัง MathWar Classic"
          >
            ⚡ MathWar Classic
          </button>
          <button
            type="button"
            className={`fullscreen-btn ${isFullscreen ? "active" : ""}`}
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            <span className="fs-icon">{isFullscreen ? "🗗" : "⛶"}</span>
            <span className="fs-text">{isFullscreen ? "Exit" : "Fullscreen"}</span>
          </button>
        </div>
      </header>

      <div className="menu-body">
        <div className="menu-col-left">
          <section className="panel">
            <h2 className="panel-title">โหมดการเล่น (Game Mode)</h2>
            <div className="mode-select">
              <button
                type="button"
                className={`mode-btn ${mode === "solo" ? "active" : ""}`}
                onClick={() => setMode("solo")}
              >
                <span className="mode-name">1.1 Solo (เดี่ยว)</span>
                <span className="mode-desc">จับเวลา 60 วินาที · เก็บคะแนน Highscore รายสัปดาห์</span>
              </button>
              <button
                type="button"
                className={`mode-btn ${mode === "duel" ? "active" : ""}`}
                onClick={() => setMode("duel")}
              >
                <span className="mode-name">1.2 Duel (ดวล 2 คน)</span>
                <span className="mode-desc">จอแบ่ง 2 ฝั่ง · แข่งความไว 60 วินาที</span>
              </button>
            </div>
          </section>

          <section className="panel">
            <h2 className="panel-title">ชื่อผู้เล่น (Players)</h2>
            <div className="name-row">
              <label className="name-field">
                <span className="name-tag">P1</span>
                <input
                  className="name-input"
                  value={p1Name}
                  onChange={(e) => setP1Name(e.target.value)}
                  maxLength={16}
                  placeholder="Player 1"
                />
              </label>
              {mode === "duel" && (
                <label className="name-field">
                  <span className="name-tag">P2</span>
                  <input
                    className="name-input"
                    value={p2Name}
                    onChange={(e) => setP2Name(e.target.value)}
                    maxLength={16}
                    placeholder="Player 2"
                  />
                </label>
              )}
            </div>
          </section>

          <div className="menu-action-buttons">
            <button type="button" className="start-btn kumon-start-btn" onClick={start}>
              {mode === "solo" ? "🚀 เริ่มเล่นโหมด Solo" : "⚔️ เริ่มการดวล (Duel)"}
            </button>

            <button
              type="button"
              className="btn secondary leaderboard-btn"
              onClick={() => openLeaderboard()}
            >
              🏆 ดูคะแนน Top 10 ประจำสัปดาห์
            </button>
          </div>
        </div>

        <div className="menu-col-right">
          <section className="panel">
            <h2 className="panel-title">เลือกความยาก (Kumon Level)</h2>

            {/* Level Selector Tabs */}
            <div className="kumon-level-tabs">
              {(["3A", "2A", "A", "B"] as KumonLevel[]).map((lvl) => {
                const isActive = activeLevelTab === lvl;
                return (
                  <button
                    key={lvl}
                    type="button"
                    className={`kumon-tab-btn ${isActive ? "active" : ""}`}
                    onClick={() => {
                      setActiveLevelTab(lvl);
                      // Auto select first category in this level
                      const firstCat = KUMON_CATEGORIES.find((c) => c.level === lvl);
                      if (firstCat) setSelectedCatId(firstCat.id);
                    }}
                  >
                    <span className="tab-lvl-title">{lvl}</span>
                  </button>
                );
              })}
            </div>

            {/* Sub-modes for Selected Level */}
            <div className="kumon-category-container">
              {activeLevelTab === "3A" && (
                <div className="kumon-sub-group">
                  <div className="kumon-group-header">
                    <span className="kumon-tag tag-3a">3A</span>
                    <span className="kumon-group-desc">การบวกไม่เกิน +3</span>
                  </div>
                  {level3ACats.map((cat) => {
                    const isSelected = selectedCatId === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        className={`kumon-item-btn ${isSelected ? "active" : ""}`}
                        onClick={() => setSelectedCatId(cat.id)}
                      >
                        <div className="kumon-item-main">
                          <span className="kumon-item-title">{cat.label}</span>
                          <span className="kumon-item-badge">{cat.subLabel}</span>
                        </div>
                        <p className="kumon-item-desc">{cat.description}</p>
                      </button>
                    );
                  })}
                </div>
              )}

              {activeLevelTab === "2A" && (
                <div className="kumon-sub-group">
                  <div className="kumon-group-header">
                    <span className="kumon-tag tag-2a">2A</span>
                    <span className="kumon-group-desc">การบวกไม่เกิน +10 และผลบวกไม่เกิน 20</span>
                  </div>
                  {level2ACats.map((cat) => {
                    const isSelected = selectedCatId === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        className={`kumon-item-btn ${isSelected ? "active" : ""}`}
                        onClick={() => setSelectedCatId(cat.id)}
                      >
                        <div className="kumon-item-main">
                          <span className="kumon-item-title">{cat.label}</span>
                          <span className="kumon-item-badge">{cat.subLabel}</span>
                        </div>
                        <p className="kumon-item-desc">{cat.description}</p>
                      </button>
                    );
                  })}
                </div>
              )}

              {activeLevelTab === "A" && (
                <div className="kumon-sub-group">
                  <div className="kumon-group-header">
                    <span className="kumon-tag tag-a">A</span>
                    <span className="kumon-group-desc">เลือก 1 ใน 3 โหมด</span>
                  </div>
                  <div className="kumon-grid-cards">
                    {levelACats.map((cat, idx) => {
                      const isSelected = selectedCatId === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          className={`kumon-item-btn ${isSelected ? "active" : ""}`}
                          onClick={() => setSelectedCatId(cat.id)}
                        >
                          <div className="kumon-item-main">
                            <span className="kumon-item-title">
                              2.3.{idx + 1} {cat.label}
                            </span>
                            <span className="kumon-item-badge">{cat.subLabel}</span>
                          </div>
                          <p className="kumon-item-desc">{cat.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeLevelTab === "B" && (
                <div className="kumon-sub-group">
                  <div className="kumon-group-header">
                    <span className="kumon-tag tag-b">B</span>
                    <span className="kumon-group-desc">เลือกโหมดบวก หรือ โหมดลบ พร้อมความยาก</span>
                  </div>

                  {/* Addition Submodes */}
                  <div className="kumon-b-subheading">➕ 2.4.1 โหมดบวก (Addition)</div>
                  <div className="kumon-diff-row">
                    {levelBCatsAdd.map((cat) => {
                      const isSelected = selectedCatId === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          className={`kumon-diff-btn ${isSelected ? "active" : ""}`}
                          onClick={() => setSelectedCatId(cat.id)}
                        >
                          <span className="diff-tier">
                            {cat.difficulty === "easy"
                              ? "🟢 ง่าย"
                              : cat.difficulty === "medium"
                              ? "🟡 ปานกลาง"
                              : "🔴 ยาก"}
                          </span>
                          <span className="diff-detail">{cat.subLabel}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Subtraction Submodes */}
                  <div className="kumon-b-subheading" style={{ marginTop: "1rem" }}>
                    ➖ 2.4.2 โหมดลบ (Subtraction)
                  </div>
                  <div className="kumon-diff-row">
                    {levelBCatsSub.map((cat) => {
                      const isSelected = selectedCatId === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          className={`kumon-diff-btn ${isSelected ? "active" : ""}`}
                          onClick={() => setSelectedCatId(cat.id)}
                        >
                          <span className="diff-tier">
                            {cat.difficulty === "easy"
                              ? "🟢 ง่าย"
                              : cat.difficulty === "medium"
                              ? "🟡 ปานกลาง"
                              : "🔴 ยาก"}
                          </span>
                          <span className="diff-detail">{cat.subLabel}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="panel records">
            <div className="record-col">
              <div className="record-col-header">
                <h2 className="panel-title">
                  Top 10 Highscore ประจำสัปดาห์ ({activePreset.label})
                </h2>
                <button
                  type="button"
                  className="view-all-link"
                  onClick={() => openLeaderboard()}
                >
                  ดูทั้งหมด 🏆
                </button>
              </div>
              {highscores.length > 0 ? (
                <ol className="hs-table">
                  {highscores.slice(0, 10).map((h, i) => (
                    <li key={i} className="hs-row">
                      <span className="hs-rank">
                        {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                      </span>
                      <span className="hs-name">{h.name}</span>
                      <span className="hs-score">{h.score} pts</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="no-records">ยังไม่มีคะแนนในโหมดนี้ (เล่นเพื่อบันทึกคนแรก!)</p>
              )}
            </div>

            {duels.length > 0 && (
              <div className="record-col">
                <h2 className="panel-title">สถิติดวลล่าสุด (Recent Duels)</h2>
                <ul className="duel-list">
                  {duels.slice(0, 10).map((d, i) => (
                    <li key={i} className="duel-row">
                      <span className="duel-winner">{d.winner ? `${d.winner} ชนะ` : "เสมอ"}</span>
                      <span className="duel-score">
                        {d.p1.score} – {d.p2.score}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Top 10 Leaderboard Modal */}
      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal-content kumon-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">🏆 Top 10 Highscores ประจำสัปดาห์ (Kumon)</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>

            {/* Week History Selector */}
            <div className="modal-week-bar">
              <label className="week-label-text">
                <span className="week-icon">🗓️</span>
                <span className="week-title">เลือกสัปดาห์:</span>
                <select
                  className="week-select-dropdown"
                  value={modalWeek}
                  onChange={(e) => setModalWeek(e.target.value)}
                >
                  {availableWeeks.map((w) => (
                    <option key={w} value={w}>
                      {formatWeekLabel(w)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Category Selector inside modal */}
            <div className="modal-kumon-cat-list">
              {KUMON_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`modal-kumon-cat-btn ${modalCatId === cat.id ? "active" : ""}`}
                  onClick={() => setModalCatId(cat.id)}
                >
                  <span className="m-cat-title">{cat.label}</span>
                  <span className="m-cat-sub">{cat.subLabel}</span>
                </button>
              ))}
            </div>

            {/* Scores Table */}
            <div className="modal-scores-container">
              <div className="modal-setting-header">
                <h3 className="modal-setting-label">
                  {KUMON_CATEGORIES.find((c) => c.id === modalCatId)?.label} ·{" "}
                  {KUMON_CATEGORIES.find((c) => c.id === modalCatId)?.subLabel}
                </h3>
                <span className="modal-week-tag">{formatWeekLabel(modalWeek)}</span>
              </div>

              {modalScores.length > 0 ? (
                <ol className="hs-table modal-hs-table">
                  {modalScores.map((h, i) => (
                    <li key={i} className="hs-row modal-hs-row">
                      <span className="hs-rank">
                        {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                      </span>
                      <span className="hs-name">{h.name}</span>
                      <span className="hs-details">
                        (ถูก {h.correctCount ?? 0} / ผิด {h.wrongCount ?? 0})
                      </span>
                      <span className="hs-score">{h.score} pts</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="no-records-box">
                  <p>ยังไม่มีสถิติ Top 10 ในโหมดนี้ประจำสัปดาห์</p>
                  <span className="no-records-hint">ลองเล่นโหมดนี้เพื่อบันทึกสถิติเป็นคนแรก!</span>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => setShowModal(false)}>
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
